import { useEffect } from "react";
import { useAtomValue } from "jotai";
import { useReactFlow, useViewport } from "@xyflow/react";
import { ZOOM_LEVELS } from "@zapier/canvas-constants";
import getNextZoomOutLevel from "../utils/getNextZoomOutLevel";
import getNextZoomInLevel from "../utils/getNextZoomInLevel";
import { isEmbedAtom } from "@zapier/canvas-state";

export function useCanvasEmbedEvents() {
  const { zoom } = useViewport();
  const { fitView, zoomTo } = useReactFlow();
  const isEmbed = useAtomValue(isEmbedAtom);

  useEffect(() => {
    const handleZoomOut = () => {
      const nextZoom = getNextZoomOutLevel(zoom, ZOOM_LEVELS);
      zoomTo(nextZoom);
    };

    const handleZoomIn = () => {
      const nextZoom = getNextZoomInLevel(zoom, ZOOM_LEVELS);
      zoomTo(nextZoom);
    };

    const handleMessageFromParent = (event: MessageEvent) => {
      const { action } = event.data;

      switch (action) {
        case "zoomIn":
          handleZoomIn();
          break;
        case "zoomOut":
          handleZoomOut();
          break;
        case "zoomToFit":
          fitView();
          break;
        default:
          console.error(`Unknown action sent by iframe parent: ${action}`);
      }
    };

    if (isEmbed) {
      window.addEventListener("message", handleMessageFromParent);
    }

    return () => window.removeEventListener("message", handleMessageFromParent);
  }, [fitView, zoom, zoomTo, isEmbed]);

  useEffect(() => {
    const sendCurrentZoomLevelToParent = () => {
      const message = {
        type: "zoomLevel",
        zoom,
      };

      window.parent.postMessage(message, "*");
    };

    if (isEmbed) {
      sendCurrentZoomLevelToParent();
    }
  }, [zoom, isEmbed]);
}
